import type {HTMLButtonAttributes} from 'svelte/elements';
import type {VariantProps} from 'tailwind-variants';
import {tv} from 'tailwind-variants';
import type {Class} from 'Catalyst/class';
import {type IconProps} from 'Catalyst/components/Icon';
import {
  buttonSlotsBaseResizingH,
  buttonSlotsBaseResizingW,
} from 'Catalyst/tokens/output/button';
import {default as Button} from './Button.svelte';

export const buttonTvProps = tv({
  slots: {
    base: `
      ${buttonSlotsBaseResizingH}
      ${buttonSlotsBaseResizingW}
      rounded-[--button-base-border-radius]
      focus:outline-offset-0
      focus:ring-0
      focus-visible:ring
      focus-visible:ring-[length:--button-base-focus-ring-width]
      focus-visible:ring-[--button-base-focus-ring-color]
    `,
    label: '',
  },
  variants: {
    size: {
      lg: {
        base: `
          space-x-[--button-variants-size-lg-item-spacing]
          px-[--button-variants-size-lg-px]
          py-[--button-variants-size-lg-py]
        `,
        label: `
          [font:--button-variants-size-lg-typography]
        `,
      },
      md: {
        base: `
          space-x-[--button-variants-size-md-item-spacing]
          px-[--button-variants-size-md-px]
          py-[--button-variants-size-md-py]
        `,
        label: `
          [font:--button-variants-size-md-typography]
        `,
      },
      sm: {
        base: `
          space-x-[--button-variants-size-sm-item-spacing]
          px-[--button-variants-size-sm-px]
          py-[--button-variants-size-sm-py]
        `,
        label: `
          [font:--button-variants-size-sm-typography]
        `,
      },
      xs: {
        base: `
          space-x-[--button-variants-size-xs-item-spacing]
          px-[--button-variants-size-xs-px]
          py-[--button-variants-size-xs-py]
        `,
        label: `
          [font:--button-variants-size-xs-typography]
        `,
      },
    },
    style: {
      filled: {
        base: `
          bg-[--button-variants-style-filled-surface-default]
          *:text-[--button-variants-style-filled-on-surface-default]
          hover:bg-[--button-variants-style-filled-surface-hover]
          focus-visible:bg-[--button-variants-style-filled-surface-focus]
          active:bg-[--button-variants-style-filled-surface-active]
          disabled:bg-[--button-variants-style-filled-surface-disabled]
          *:disabled:text-[--button-variants-style-filled-on-surface-disabled]
        `,
      },
      outlined: {
        base: `
          bg-[--button-variants-style-outlined-surface-default]
          outline
          outline-1
          outline-[--button-variants-style-outlined-on-surface-default]
          *:text-[--button-variants-style-outlined-on-surface-default]
          hover:bg-[--button-variants-style-outlined-surface-hover]
          focus:outline-1
          focus:outline-[--button-variants-style-outlined-on-surface-default]
          focus-visible:bg-[--button-variants-style-outlined-surface-focus]
          focus-visible:outline-none
          active:bg-[--button-variants-style-outlined-surface-active]
          disabled:bg-[--button-variants-style-outlined-surface-disabled]
          disabled:outline-[--button-variants-style-outlined-surface-disabled]
          *:disabled:text-[--button-variants-style-outlined-on-surface-disabled]
        `,
      },
      text: {
        base: `
          bg-[--button-variants-style-text-surface-default]
          *:text-[--button-variants-style-text-on-surface-default]
          hover:bg-[--button-variants-style-text-surface-hover]
          focus-visible:bg-[--button-variants-style-text-surface-focus]
          active:bg-[--button-variants-style-text-surface-active]
          disabled:bg-[--button-variants-style-text-surface-disabled]
          *:disabled:text-[--button-variants-style-text-on-surface-disabled]
        `,
      },
      tonal: {
        base: `
          bg-[--button-variants-style-tonal-surface-default]
          *:text-[--button-variants-style-tonal-on-surface-default]
          hover:bg-[--button-variants-style-tonal-surface-hover]
          focus-visible:bg-[--button-variants-style-tonal-surface-focus]
          active:bg-[--button-variants-style-tonal-surface-active]
          disabled:bg-[--button-variants-style-tonal-surface-disabled]
          *:disabled:text-[--button-variants-style-tonal-on-surface-disabled]
        `,
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    style: 'filled',
  },
});

export type ButtonTVProps = VariantProps<typeof buttonTvProps>;

export type LabelProps = Partial<{
  labelClass: Class;
}>;

export type ButtonOwnProps = Partial<{
  label: string;
  leadingIcon: IconProps['name'];
  trailingIcon: IconProps['name'];
  href: string;
  target: string;
}>;

export type ButtonProps = HTMLButtonAttributes &
  ButtonTVProps &
  ButtonOwnProps &
  Class &
  LabelProps;

export {Button};
